import axios from 'axios';
import { Buffer } from 'buffer';
import { redirectToLogin } from '../utils/globalUtils';
import baseURL, { API_PREFIX } from './server';
import { apiTrace } from '../utils/apiTrace';
import { showToast } from '../utils/toast';
import { attachPayloadCrypto } from './payloadCrypto';

const axiosInstanceWeb = axios.create({
  baseURL: `${baseURL}/api${API_PREFIX}/casa-service/v1`,
  headers: {
    Accept: '*/*',
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

const axiosInstanceWebV2 = axios.create({
  baseURL: `${baseURL}/api${API_PREFIX}/casa-service/v2`,
  headers: {
    Accept: '*/*',
    'Content-Type': 'application/json'
  },
  withCredentials: true
});


// Payload encryption (FSP-5412). Registered before the interceptors below on purpose: axios runs
// response interceptors in registration order, so this one opens a sealed body before the handlers
// below read it, and retries a 409 from an expired session before anything can surface it to the
// user. It is inert unless the gateway reports this tenant as enabled.
// No gatewayBaseUrl on purpose: the library derives `${window.location.origin}/api/gw`
// itself, which is correct for every tenant domain. Passing one overrides that.
attachPayloadCrypto(axiosInstanceWeb, { axios });
attachPayloadCrypto(axiosInstanceWebV2, { axios });

if (sessionStorage.getItem('authorization')) {
  const token = sessionStorage.getItem('authorization');
  const decryptedToken = Buffer.from(token, 'base64');
  axiosInstanceWeb.defaults.headers.common['authorization'] = decryptedToken;
  axiosInstanceWebV2.defaults.headers.common['authorization'] = decryptedToken;
}

const handleInstanceResponse = (response) => {
  if (response.headers?.authorization) {
    const encryptedToken = Buffer.from(response.headers.authorization).toString('base64');
    sessionStorage.setItem('authorization', encryptedToken);
    axiosInstanceWeb.defaults.headers.common['authorization'] = Buffer.from(
      encryptedToken,
      'base64'
    );
    axiosInstanceWebV2.defaults.headers.common['authorization'] = Buffer.from(
      encryptedToken,
      'base64'
    );
  }
  apiTrace(response);
  return response;
};

const handleInstanceError = (error) => {
  const status = error?.response?.status;
  const message = error?.response?.data?.message;

  // Handle authentication
  if (status === 401) {
    redirectToLogin();
    return Promise.reject(error);
  }

  // Show user-facing error toast for all other errors
  // Specific error messages from server, or fallback generic message
  const errorMessage = message || getDefaultErrorMessage(status);
  showToast('error', errorMessage);

  return Promise.reject(error);
};

// Map HTTP status codes to user-friendly messages
function getDefaultErrorMessage(status) {
  switch (status) {
    case 400:
      return 'Invalid request. Please check your input and try again.';
    case 403:
      return "You don't have permission to perform this action.";
    case 404:
      return 'The requested resource was not found.';
    case 409:
      return 'This action conflicts with the current state. Please refresh and try again.';
    case 500:
    case 502:
    case 503:
      return 'Server error. Please try again later.';
    default:
      return 'Something went wrong. Please try again.';
  }
}

axiosInstanceWeb.interceptors.response.use(handleInstanceResponse, handleInstanceError);
axiosInstanceWebV2.interceptors.response.use(handleInstanceResponse, handleInstanceError);

export { axiosInstanceWeb, axiosInstanceWebV2 };
